import { Dialog } from '@headlessui/react';
import { FiAlertTriangle, FiCheckCircle, FiX } from 'react-icons/fi';

interface ConfirmationDialogProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
  readonly title: string;
  readonly message: string;
  readonly confirmText?: string;
  readonly cancelText?: string;
  readonly type?: 'warning' | 'info' | 'success';
  readonly loading?: boolean;
  readonly existingData?: readonly any[];
}

export default function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning',
  loading = false,
  existingData = []
}: ConfirmationDialogProps) {
  const getIcon = () => {
    switch (type) {
      case 'warning':
        return <FiAlertTriangle className="w-6 h-6 text-yellow-600" />;
      case 'success':
        return <FiCheckCircle className="w-6 h-6 text-green-600" />;
      default:
        return <FiAlertTriangle className="w-6 h-6 text-blue-600" />;
    }
  };

  const getIconBgColor = () => {
    switch (type) {
      case 'warning':
        return 'bg-yellow-100';
      case 'success':
        return 'bg-green-100';
      default:
        return 'bg-blue-100';
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-[100]">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />

      {/* Dialog Container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>

          {/* Icon and Title */}
          <div className="flex items-start space-x-4">
            <div className={`flex-shrink-0 w-10 h-10 rounded-full ${getIconBgColor()} flex items-center justify-center`}>
              {getIcon()}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {title}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {message}
              </p>

              {/* Show existing data if available */}
              {existingData.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Existing Risk Factors ({existingData.length}):
                  </h4>
                  <div className="max-h-32 overflow-y-auto bg-gray-50 rounded-md p-3">
                    {existingData.map((item, index) => (
                      <div key={item.id || item.name || `risk-factor-${index}`} className="text-xs text-gray-600 py-1">
                        • {item.name || item.id || `Item ${index + 1}`}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={loading}
                >
                  {cancelText}
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {loading && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  <span>{confirmText}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
